'use client';

import Link from 'next/link';
import { useState } from 'react';
import { PATH } from '@/constants';
import type { CaseStudy } from '../data';
import { FEATURED_STUDIES } from '../data';
import { Avatar, LinePill } from './Atoms';

function QuoteCard({ item }: { item: CaseStudy }) {
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 22,
        padding: 'clamp(28px, 3vw, 44px)',
        boxShadow: '0 36px 70px -28px rgba(2,16,55,.32)',
        border: '1px solid var(--mb-border)',
      }}
    >
      <Link
        href={`${PATH.CASE_STUDIES}/${item.slug}`}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 10, textDecoration: 'none', marginBottom: 24 }}
      >
        <span
          style={{
            fontFamily: 'var(--mb-font-display)',
            fontWeight: 700,
            fontSize: 19,
            letterSpacing: '-0.01em',
            color: 'var(--mb-slate-900)',
          }}
        >
          {item.customer}
        </span>
        <span style={{ color: 'var(--mb-blue-600)', fontSize: 18 }}>→</span>
      </Link>
      <p
        style={{
          fontFamily: 'var(--mb-font-display)',
          fontWeight: 500,
          fontSize: 'clamp(1.15rem, 1.7vw, 1.5rem)',
          lineHeight: 1.45,
          letterSpacing: '-0.01em',
          color: 'var(--mb-slate-900)',
          margin: '0 0 28px',
        }}
      >
        &ldquo;{item.quote}&rdquo;
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <Avatar initials={item.quoteInitials} src={item.quoteImage} alt={item.quoteAuthor} size={50} />
        <div>
          <div style={{ fontWeight: 600, fontSize: 14.5, color: 'var(--mb-slate-900)' }}>{item.quoteAuthor}</div>
          <div style={{ fontSize: 13.5, color: 'var(--mb-slate-500)' }}>{item.quoteTitle}</div>
        </div>
      </div>
    </div>
  );
}

function StatBadge({ stat, label }: { stat: string; label: string }) {
  return (
    <div
      style={{
        position: 'absolute',
        right: -10,
        bottom: -28,
        background: 'linear-gradient(150deg, #3D6AFE, #0639BA)',
        borderRadius: 18,
        padding: '20px 26px',
        color: '#fff',
        boxShadow: '0 26px 44px -18px rgba(8,72,231,.5)',
        maxWidth: 210,
        zIndex: 2,
      }}
    >
      <div
        style={{
          fontFamily: 'var(--mb-font-display)',
          fontWeight: 700,
          fontSize: 'clamp(2rem, 2.6vw, 2.6rem)',
          lineHeight: 1,
          letterSpacing: '-0.02em',
        }}
      >
        {stat}
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.4, color: 'rgba(255,255,255,.88)', marginTop: 8 }}>{label}</div>
    </div>
  );
}

export default function FeaturedCarousel() {
  const [idx, setIdx] = useState(0);
  const item = FEATURED_STUDIES[idx];
  const go = (dir: 1 | -1) => setIdx((p) => (p + dir + FEATURED_STUDIES.length) % FEATURED_STUDIES.length);

  return (
    <section style={{ padding: 'clamp(16px, 2.5vw, 32px) clamp(20px, 5vw, 80px)' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div
          style={{
            background: 'linear-gradient(180deg, #EEF3FF 0%, #F5F8FF 100%)',
            borderRadius: 28,
            padding: 'clamp(36px, 5vw, 72px) clamp(28px, 4vw, 60px)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Subtle scribble accents (echoes the reference design) */}
          <svg
            aria-hidden
            viewBox="0 0 1180 560"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', opacity: 0.45 }}
          >
            <g stroke="var(--mb-blue-600)" strokeWidth="2.5" strokeLinecap="round" fill="none" opacity="0.5">
              <path d="M70 150 q12 -10 26 -2" />
              <path d="M58 260 l28 6" />
              <path d="M1120 190 l-24 10" />
              <path d="M1128 310 q-10 8 -26 4" />
            </g>
            <g stroke="var(--mb-blue-600)" strokeWidth="2.5" strokeLinecap="round" fill="none" opacity="0.3">
              <path d="M42 205 l10 0 M47 200 l0 10" />
              <path d="M1138 248 l10 0 M1143 243 l0 10" />
            </g>
          </svg>

          <div
            className="cs-feat-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1.08fr',
              gap: 'clamp(32px, 5vw, 72px)',
              alignItems: 'center',
              position: 'relative',
              zIndex: 1,
            }}
          >
            {/* Left: story text */}
            <div>
              <div style={{ marginBottom: 20 }}>
                <LinePill line={item.line} />
              </div>
              <h2
                style={{
                  fontFamily: 'var(--mb-font-display)',
                  fontWeight: 500,
                  fontSize: 'clamp(1.85rem, 3.2vw, 2.9rem)',
                  lineHeight: 1.1,
                  letterSpacing: '-0.02em',
                  color: 'var(--mb-slate-900)',
                  margin: 0,
                }}
              >
                {item.headlinePre}
                <span style={{ color: 'var(--mb-blue-600)' }}>{item.headlineHi}</span>
                {item.headlinePost}
              </h2>
              <p
                style={{
                  fontSize: 'clamp(1rem, 1.15vw, 1.1rem)',
                  lineHeight: 1.65,
                  color: 'var(--mb-slate-600)',
                  margin: '22px 0 0',
                  maxWidth: 460,
                }}
              >
                {item.dek}
              </p>
              <Link
                href={`${PATH.CASE_STUDIES}/${item.slug}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  marginTop: 28,
                  padding: '12px 22px',
                  borderRadius: 14,
                  textDecoration: 'none',
                  fontSize: 15,
                  fontWeight: 600,
                  background: 'var(--mb-blue-600)',
                  color: '#fff',
                  boxShadow: 'var(--mb-shadow-cta)',
                }}
              >
                Read case study <span>›</span>
              </Link>

              {/* Carousel controls */}
              <div style={{ display: 'flex', gap: 10, marginTop: 36, alignItems: 'center' }}>
                {(['prev', 'next'] as const).map((dir) => (
                  <button
                    key={dir}
                    onClick={() => go(dir === 'prev' ? -1 : 1)}
                    aria-label={dir === 'prev' ? 'Previous story' : 'Next story'}
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      cursor: 'pointer',
                      border: '1px solid var(--mb-border-strong)',
                      background: '#fff',
                      color: 'var(--mb-slate-700)',
                      fontSize: 18,
                      display: 'grid',
                      placeItems: 'center',
                    }}
                  >
                    {dir === 'prev' ? '‹' : '›'}
                  </button>
                ))}
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginLeft: 6 }}>
                  {FEATURED_STUDIES.map((s, k) => (
                    <button
                      key={s.slug}
                      onClick={() => setIdx(k)}
                      aria-label={`Go to ${s.customer}`}
                      style={{
                        width: k === idx ? 22 : 8,
                        height: 8,
                        padding: 0,
                        border: 'none',
                        cursor: 'pointer',
                        borderRadius: 999,
                        background: k === idx ? 'var(--mb-blue-600)' : 'var(--mb-slate-300)',
                        transition: 'width 200ms ease',
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Right: quote card + floating stat badge */}
            <div style={{ position: 'relative' }}>
              <QuoteCard item={item} />
              <StatBadge stat={item.stat} label={item.statLabel} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
